<template>
  <r-ai-page-container>
    <!-- PR-UPGRADE-MODAL: per-tool premium-banner replaced by the one shared
         UpgradeToPlusModal (mounted globally in App.vue). Gating below is
         unchanged; it opens the shared modal via `upgradeModal/open`. -->
    <v-row class="text-start">
      <v-col cols="12">
        <h1 class="mb-2">AI Career Story Generator</h1>
        <p class="text-body-1 text--secondary mb-4">
          Transform your experiences into compelling career narratives. Generate LinkedIn summaries, elevator pitches, and more.
        </p>


        <v-card v-if="loading" class="pa-6 mb-4 text-center" outlined>
          <v-icon color="primary" large class="mb-3">mdi-account-circle</v-icon>
          <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
          <div class="text-body-2 text--secondary">Loading your profile data...</div>
        </v-card>

        <v-card v-else-if="profileError" class="pa-4 mb-4" outlined color="warning">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="warning" class="mr-3">mdi-alert</v-icon>
              <div>
                <div class="font-weight-medium">Could not load profile</div>
                <div class="text-body-2 text--secondary">{{ profileError }}</div>
              </div>
            </div>
            <v-btn small text color="primary" @click="loadProfileContext">
              <v-icon left small>mdi-refresh</v-icon>
              Retry
            </v-btn>
          </div>
        </v-card>

        <v-card v-else class="pa-4 mb-4" outlined>
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-3">mdi-account-check</v-icon>
              <div>
                <div class="font-weight-medium">{{ profileDisplayName }}</div>
                <div class="text-body-2 text--secondary">{{ completenessPercentage }}% complete</div>
              </div>
            </div>
            <div class="d-flex flex-wrap mt-2 mt-sm-0">
              <v-chip
                small
                outlined
                class="mr-2 mb-1 stat-chip-clickable"
                color="primary"
                role="button"
                tabindex="0"
                @click="goToStat('/Work')"
                @keyup.enter="goToStat('/Work')"
              >
                <v-icon small left>mdi-briefcase</v-icon>
                {{ evidenceCounts.workExperience }} jobs
              </v-chip>
              <v-chip
                small
                outlined
                class="mr-2 mb-1 stat-chip-clickable"
                color="primary"
                role="button"
                tabindex="0"
                @click="goToStat('/HardSkills')"
                @keyup.enter="goToStat('/HardSkills')"
              >
                <v-icon small left>mdi-lightbulb</v-icon>
                {{ evidenceCounts.skills }} skills
              </v-chip>
              <v-chip
                small
                outlined
                class="mr-2 mb-1 stat-chip-clickable"
                color="primary"
                role="button"
                tabindex="0"
                @click="goToStat('/Work')"
                @keyup.enter="goToStat('/Work')"
              >
                <v-icon small left>mdi-trophy</v-icon>
                {{ evidenceCounts.achievements }} achievements
              </v-chip>
              <v-chip
                small
                outlined
                class="mb-1 stat-chip-clickable"
                color="primary"
                role="button"
                tabindex="0"
                @click="goToStat('/SmallWinsProjects')"
                @keyup.enter="goToStat('/SmallWinsProjects')"
              >
                <v-icon small left>mdi-star</v-icon>
                {{ evidenceCounts.smallWins }} wins
              </v-chip>
            </div>
          </div>

          <v-progress-linear
            :value="completenessPercentage"
            :color="completenessColor"
            height="6"
            rounded
            class="mt-3"
          ></v-progress-linear>

          <v-expand-transition>
            <div v-if="showProfilePrompts && missingItems.length > 0" class="mt-3">
              <div class="text-caption text--secondary mb-2">Add more for richer stories:</div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="item in missingItems.slice(0, 3)"
                  :key="item"
                  small
                  outlined
                  color="warning"
                  class="mr-2 mb-1"
                  @click="goToMissing(item)"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  {{ item }}
                </v-chip>
              </div>
            </div>
          </v-expand-transition>

          <v-btn
            v-if="missingItems.length > 0"
            x-small
            text
            color="primary"
            class="mt-2"
            @click="showProfilePrompts = !showProfilePrompts"
          >
            {{ showProfilePrompts ? "Hide suggestions" : "Improve your story" }}
          </v-btn>
        </v-card>

        <v-tabs v-model="activeTab" class="mb-4" show-arrows>
          <v-tab>My LinkedIn Profile</v-tab>
          <v-tab>LinkedIn Summary</v-tab>
          <v-tab>LinkedIn Headline</v-tab>
          <v-tab>LinkedIn Projects</v-tab>
          <v-tab>Superpowers</v-tab>
          <v-tab>Elevator Pitch</v-tab>
          <v-tab>Career Narrative</v-tab>
          <v-tab>Biography</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <v-card class="pa-4" outlined>
              <div class="d-flex justify-space-between align-center mb-4">
                <h3>My LinkedIn Profile</h3>
                <v-btn small color="primary" outlined @click="copyAllLinkedIn">
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy All Sections
                </v-btn>
              </div>
              <p class="text-body-2 text--secondary mb-6">
                Your assembled LinkedIn profile — pulled from your Resumation data. Generate individual sections using the tabs above, then come back here to see the full picture.
              </p>

              <div class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Headline</div>
                  <v-btn v-if="selectedHeadline" icon x-small @click="copyToClipboard(selectedHeadline)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-if="selectedHeadline" class="text-body-1 font-weight-medium">{{ selectedHeadline }}</div>
                <div v-else class="text-body-2 text--secondary">
                  No headline generated yet.
                  <a href="#" @click.prevent="activeTab = 2" class="primary--text">Generate headlines</a>
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">About</div>
                  <v-btn v-if="linkedInSummary" icon x-small @click="copyToClipboard(linkedInSummary)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-if="linkedInSummary" class="text-body-1" style="white-space: pre-wrap;">{{ linkedInSummary }}</div>
                <div v-else class="text-body-2 text--secondary">
                  No summary generated yet.
                  <a href="#" @click.prevent="activeTab = 1" class="primary--text">Generate a summary</a>
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Experience</div>
                  <div class="d-flex align-center flex-wrap" style="gap: 4px;">
                    <v-btn-toggle v-if="displayExperience.length" v-model="experienceStyle" dense mandatory class="mr-2" :disabled="generatingExperience">
                      <v-btn x-small value="original" :outlined="experienceStyle !== 'original'">Original</v-btn>
                      <v-btn x-small value="optimized" :outlined="experienceStyle !== 'optimized'" @click="onOptimizedToggle">Optimized</v-btn>
                    </v-btn-toggle>
                    <v-btn v-if="displayExperience.length && experienceStyle === 'optimized'"
                      small color="primary" text :loading="generatingExperience" :disabled="generatingExperience" @click="optimizeExperience">
                      <v-icon small left>mdi-sparkles</v-icon>
                      {{ optimizedExperienceData.optimized ? 'Re-optimize' : 'Optimize with AI' }}
                    </v-btn>
                    <v-btn v-if="displayExperience.length" icon x-small @click="copyToClipboard(experienceAsText)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
                <!-- ITEM-4A: explicit, mobile-visible working state for the
                     LinkedIn experience optimize call. The text-button spinner
                     alone is easy to miss on a phone, so show a clear inline
                     progress row while the call is in flight. -->
                <v-alert
                  v-if="generatingExperience"
                  dense
                  text
                  type="info"
                  class="mb-2 d-flex align-center optimize-working"
                >
                  <div class="d-flex align-center">
                    <v-progress-circular
                      indeterminate
                      size="16"
                      width="2"
                      color="primary"
                      class="mr-2"
                    ></v-progress-circular>
                    Optimizing your experience with AI. This can take a moment.
                  </div>
                </v-alert>
                <div v-if="displayExperience.length">
                  <v-card v-for="(job, ji) in displayExperience" :key="'exp-' + ji + '-' + experienceStyle" class="pa-3 mb-3" flat outlined>
                    <div class="font-weight-bold">{{ job.title }}</div>
                    <div class="text-body-2 text--secondary">{{ job.company }}<span v-if="job.dates"> | {{ job.dates }}</span></div>
                    <ul v-if="job.achievements && job.achievements.length" class="text-body-2 pl-4 mt-2">
                      <li v-for="(ach, ai) in job.achievements" :key="'ach-' + ai" class="mb-1">{{ ach }}</li>
                    </ul>
                  </v-card>
                </div>
                <div v-else class="text-body-2 text--secondary">
                  No work experience in your profile yet.
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Skills</div>
                  <v-btn v-if="assembledSkills.length" icon x-small @click="copyToClipboard(assembledSkills.join(' \u00B7 '))">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-if="assembledSkills.length" class="d-flex flex-wrap">
                  <v-chip v-for="(skill, si) in assembledSkills" :key="'sk-' + si" small outlined class="mr-2 mb-2">{{ skill }}</v-chip>
                </div>
                <div v-else class="text-body-2 text--secondary">
                  No skills in your profile yet.
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Education</div>
                  <v-btn v-if="assembledEducation.length" icon x-small @click="copyToClipboard(educationAsText)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-if="assembledEducation.length">
                  <div v-for="(edu, ei) in assembledEducation" :key="'edu-' + ei" class="mb-2">
                    <div class="font-weight-medium">{{ edu.degree }}</div>
                    <div class="text-body-2 text--secondary">{{ edu.school }}<span v-if="edu.year"> | {{ edu.year }}</span></div>
                  </div>
                </div>
                <div v-else class="text-body-2 text--secondary">
                  No education in your profile yet.
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div v-if="assembledCertifications.length" class="mb-6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Licenses & Certifications</div>
                  <v-btn icon x-small @click="copyToClipboard(assembledCertifications.join('\n'))">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-for="(cert, ci) in assembledCertifications" :key="'cert-' + ci" class="text-body-2 mb-1">
                  {{ cert }}
                </div>
                <v-divider class="mt-4"></v-divider>
              </div>

              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="text-overline">Projects</div>
                  <v-btn v-if="linkedInProjects.length" icon x-small @click="copyToClipboard(projectsAsText)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div v-if="linkedInProjects.length">
                  <v-card v-for="(project, pi) in linkedInProjects" :key="'proj-' + pi" class="pa-3 mb-3" flat outlined>
                    <div class="font-weight-bold">{{ project.projectName }}</div>
                    <div class="text-body-2 mt-1" style="white-space: pre-wrap;">{{ project.description }}</div>
                  </v-card>
                </div>
                <div v-else class="text-body-2 text--secondary">
                  No projects generated yet.
                  <a href="#" @click.prevent="activeTab = 3" class="primary--text">Find project-worthy accomplishments</a>
                </div>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">LinkedIn Summary</h3>
              <p class="text-body-2 text--secondary mb-4">
                A professional summary that highlights your value proposition and career journey.
              </p>
              
              <v-text-field
                v-model="targetRole"
                label="Target Role (optional)"
                placeholder="e.g., Senior Product Manager, Engineering Lead"
                outlined
                dense
                class="mb-4"
              ></v-text-field>

              <v-select
                v-model="tone"
                :items="toneOptions"
                label="Tone"
                outlined
                dense
                class="mb-4"
              ></v-select>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData"
                @click="generateLinkedInSummary"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Generate Summary
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or achievements to generate a story
              </div>

              <div v-if="linkedInSummary" class="mt-6">
                <v-card class="pa-4 bg-grey-lighten-4" outlined>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <span class="text-overline">Generated Summary</span>
                    <v-btn icon small @click="copyToClipboard(linkedInSummary)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="text-body-1" style="white-space: pre-wrap;">{{ linkedInSummary }}</p>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">LinkedIn Headline</h3>
              <p class="text-body-2 text--secondary mb-4">
                Your headline is what people see first on LinkedIn — in search results, connection requests, and comments. Make it count. Max 220 characters.
              </p>

              <v-text-field
                v-model="headlineTargetRole"
                label="Target Role (optional)"
                placeholder="e.g., VP of Engineering, Product Strategy Leader"
                outlined
                dense
                class="mb-4"
              ></v-text-field>

              <v-text-field
                v-model="headlineIndustry"
                label="Industry (optional)"
                placeholder="e.g., Energy, Financial Services, Manufacturing"
                outlined
                dense
                class="mb-4"
              ></v-text-field>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData"
                @click="generateLinkedInHeadline"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Generate Headlines
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or skills to generate a headline
              </div>

              <div v-if="linkedInHeadlines.length" class="mt-6">
                <div class="text-overline mb-3">Choose Your Headline</div>
                <v-card
                  v-for="(headline, index) in linkedInHeadlines"
                  :key="index"
                  class="pa-4 mb-3"
                  :class="{ 'headline-selected': selectedHeadlineIndex === index }"
                  outlined
                  @click="selectedHeadlineIndex = index"
                  style="cursor: pointer;"
                >
                  <div class="d-flex justify-space-between align-start">
                    <div class="flex-grow-1">
                      <p class="text-body-1 mb-1" style="white-space: pre-wrap;">{{ headline }}</p>
                      <span class="text-caption" :class="headline.length > 220 ? 'error--text' : 'text--secondary'">
                        {{ headline.length }}/220 characters
                      </span>
                    </div>
                    <v-btn icon small @click.stop="copyToClipboard(headline)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">LinkedIn Projects</h3>
              <p class="text-body-2 text--secondary mb-4">
                The Projects section showcases your biggest initiatives — work that goes beyond your day-to-day role. We'll find the best candidates from your achievements and small wins.
              </p>

              <v-alert type="info" text dense class="mb-4">
                Your role descriptions and accomplishment bullets are already in your Resumation Profile for the Experience section. Projects here are for <strong>additional standout work</strong> that doesn't fit neatly under one job.
              </v-alert>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData"
                @click="generateLinkedInProjects"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Find Project-Worthy Accomplishments
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add achievements or small wins to find project candidates
              </div>

              <div v-if="linkedInProjects.length" class="mt-6">
                <div class="text-overline mb-3">Recommended LinkedIn Projects</div>
                <v-card
                  v-for="(project, index) in linkedInProjects"
                  :key="index"
                  class="pa-4 mb-4"
                  outlined
                >
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <h4 class="mb-1">{{ project.projectName }}</h4>
                      <v-chip x-small outlined color="primary" class="mr-2">{{ project.sourceType }}</v-chip>
                    </div>
                    <v-btn icon small @click="copyProjectToClipboard(project)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="text-body-2 mb-2" style="white-space: pre-wrap;">{{ project.description }}</p>
                  <div class="text-caption text--secondary">
                    <v-icon x-small class="mr-1">mdi-lightbulb-outline</v-icon>
                    {{ project.whyProjectWorthy }}
                  </div>
                  <div v-if="project.sourceDescription" class="text-caption text--secondary mt-1">
                    <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
                    Source: {{ project.sourceDescription }}
                  </div>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">Your Superpowers</h3>
              <p class="text-body-2 text--secondary mb-4">
                Discover the distinctive capabilities that set you apart — backed by real evidence from your career.
              </p>

              <v-radio-group v-model="superpowerScope" row class="mb-4">
                <v-radio label="Entire Career" value="career"></v-radio>
                <v-radio label="For a Specific Role" value="role"></v-radio>
              </v-radio-group>

              <v-combobox
                v-if="superpowerScope === 'role'"
                v-model="superpowerTargetRole"
                :items="profileJobOptions"
                label="Select a role or type a custom one"
                placeholder="Pick from your jobs or type a target role"
                outlined
                dense
                class="mb-4"
              ></v-combobox>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData || (superpowerScope === 'role' && !superpowerTargetRole)"
                @click="generateSuperPowers"
              >
                <v-icon left>mdi-flash</v-icon>
                Discover Superpowers
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or achievements to discover your superpowers
              </div>

              <div v-if="superpowers.length > 0" class="mt-6">
                <div v-for="(sp, idx) in superpowers" :key="'sp-' + idx" class="mb-4">
                  <v-card class="pa-4 bg-grey-lighten-4" outlined>
                    <div class="d-flex justify-space-between align-start mb-2">
                      <div>
                        <div class="d-flex align-center mb-1">
                          <v-icon color="amber darken-2" class="mr-2">mdi-flash</v-icon>
                          <span class="text-h6 font-weight-bold">{{ sp.name }}</span>
                        </div>
                        <p class="text-body-1 text--secondary mb-0">{{ sp.oneLiner }}</p>
                      </div>
                      <v-btn icon small @click="copySuperpower(sp)">
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </div>

                    <div class="mt-3">
                      <div class="text-overline mb-1">Evidence</div>
                      <ul class="text-body-2 pl-4">
                        <li v-for="(ev, ei) in sp.evidence" :key="'ev-' + ei" class="mb-1">{{ ev }}</li>
                      </ul>
                    </div>

                    <div v-if="sp.roles && sp.roles.length > 0" class="mt-3">
                      <div class="text-overline mb-1">Where It Showed Up</div>
                      <v-chip
                        v-for="(role, ri) in sp.roles"
                        :key="'role-' + ri"
                        small
                        outlined
                        class="mr-2 mb-1"
                      >{{ role }}</v-chip>
                    </div>
                  </v-card>
                </div>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">Elevator Pitch</h3>
              <p class="text-body-2 text--secondary mb-4">
                A 30-60 second introduction that captures who you are and what you bring.
              </p>

              <v-select
                v-model="pitchContext"
                :items="pitchContextOptions"
                label="Context"
                outlined
                dense
                class="mb-4"
              ></v-select>

              <v-slider
                v-model="pitchDuration"
                :min="15"
                :max="90"
                :step="15"
                label="Duration (seconds)"
                thumb-label
                class="mb-4"
              ></v-slider>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData"
                @click="generateElevatorPitch"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Generate Pitch
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or achievements to generate a story
              </div>

              <div v-if="elevatorPitch" class="mt-6">
                <v-card class="pa-4 bg-grey-lighten-4" outlined>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <span class="text-overline">Generated Pitch (~{{ pitchDuration }}s)</span>
                    <v-btn icon small @click="copyToClipboard(elevatorPitch)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="text-body-1" style="white-space: pre-wrap;">{{ elevatorPitch }}</p>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">Career Narrative</h3>
              <p class="text-body-2 text--secondary mb-4">
                A cohesive story that connects your experiences and explains your career trajectory.
              </p>

              <v-select
                v-model="narrativeStyle"
                :items="narrativeStyleOptions"
                label="Style"
                outlined
                dense
                class="mb-4"
              ></v-select>

              <v-checkbox
                v-model="includeTransitions"
                label="Emphasize career transitions and growth"
                class="mb-4"
              ></v-checkbox>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData"
                @click="generateCareerNarrative"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Generate Narrative
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or achievements to generate a story
              </div>

              <div v-if="careerNarrative" class="mt-6">
                <v-card class="pa-4 bg-grey-lighten-4" outlined>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <span class="text-overline">Generated Narrative</span>
                    <v-btn icon small @click="copyToClipboard(careerNarrative)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="text-body-1" style="white-space: pre-wrap;">{{ careerNarrative }}</p>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="pa-4" outlined>
              <h3 class="mb-3">Biography</h3>
              <p class="text-body-2 text--secondary mb-4">
                A professional biography for conference programs, board nominations, company websites, award submissions, or any other use.
              </p>

              <v-textarea
                v-model="bioInstructions"
                label="Instructions (optional)"
                placeholder="e.g., This is for a board nomination — keep it formal and emphasize governance experience. Or: Short bio for a podcast appearance, keep it conversational."
                outlined
                dense
                rows="3"
                :counter="3000"
                :rules="[v => !v || v.length <= 3000 || 'Instructions must be 3,000 characters or less']"
                class="mb-4"
              ></v-textarea>

              <v-select
                v-model="bioLength"
                :items="bioLengthOptions"
                label="Length"
                outlined
                dense
                class="mb-4"
              ></v-select>

              <v-select
                v-model="bioTone"
                :items="bioToneOptions"
                label="Tone"
                outlined
                dense
                class="mb-4"
              ></v-select>

              <v-btn
                color="primary"
                :loading="generating"
                :disabled="!hasEnoughData || (bioInstructions && bioInstructions.length > 3000)"
                @click="generateBiography"
              >
                <v-icon left>mdi-sparkles</v-icon>
                Generate Biography
              </v-btn>
              <div v-if="!hasEnoughData" class="text-caption warning--text mt-2">
                Add some work experience or achievements to generate a biography
              </div>

              <div v-if="biography" class="mt-6">
                <v-card class="pa-4 bg-grey-lighten-4" outlined>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <span class="text-overline">Generated Biography</span>
                    <v-btn icon small @click="copyToClipboard(biography)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="text-body-1" style="white-space: pre-wrap;">{{ biography }}</p>
                </v-card>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>

        <div class="mt-6">
          <v-btn outlined @click="$router.push('/SmallWinsCapture')">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Quick Capture
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";
import * as CareerStoryApi from "../../api/CareerStoryApi";
import { GetProfileContext } from "../../api/ProfileContextApi";
import {
  superpowersFingerprint as buildSuperpowersFingerprint,
  writeSuperpowersRecord
} from "../../utility/superpowers";

export default {
  name: "CareerStoryGenerator",
  data() {
    return {
      loading: true,
      profileError: null,
      profileContext: {},
      showProfilePrompts: false,
      activeTab: 0,
      generating: false,
      targetRole: "",
      tone: "professional",
      toneOptions: [
        { text: "Professional", value: "professional" },
        { text: "Conversational", value: "conversational" },
        { text: "Executive", value: "executive" },
        { text: "Creative", value: "creative" }
      ],
      linkedInSummary: "",
      pitchContext: "networking",
      pitchContextOptions: [
        { text: "Networking Event", value: "networking" },
        { text: "Job Interview", value: "interview" },
        { text: "Conference", value: "conference" },
        { text: "Casual Introduction", value: "casual" }
      ],
      pitchDuration: 30,
      elevatorPitch: "",
      narrativeStyle: "chronological",
      narrativeStyleOptions: [
        { text: "Chronological Journey", value: "chronological" },
        { text: "Theme-Based", value: "thematic" },
        { text: "Challenge-Overcome", value: "challenge" },
        { text: "Future-Focused", value: "future" }
      ],
      includeTransitions: true,
      careerNarrative: "",
      bioInstructions: "",
      bioLength: "medium",
      bioLengthOptions: [
        { text: "Short (~50-75 words)", value: "short" },
        { text: "Medium (~125-175 words)", value: "medium" },
        { text: "Full (~250-350 words)", value: "full" }
      ],
      bioTone: "formal",
      bioToneOptions: [
        { text: "Formal", value: "formal" },
        { text: "Conversational", value: "conversational" },
        { text: "Executive", value: "executive" }
      ],
      biography: "",
      headlineTargetRole: "",
      headlineIndustry: "",
      linkedInHeadlines: [],
      selectedHeadlineIndex: -1,
      linkedInProjects: [],
      superpowerScope: "career",
      superpowerTargetRole: "",
      superpowers: [],
      experienceStyle: "original",
      optimizedExperienceData: {},
      generatingExperience: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success"
    };
  },
  computed: {
    ...mapFields(["resumeId"]),
    ...mapFields("auth", { isPremium: "isPremium" }),
    ...mapState("settings", ["companyViewEnabled"]),
    profileDisplayName() {
      const ctx = this.profileContext;
      if (ctx.displayName) return ctx.displayName;
      const first = ctx.name?.firstName || '';
      const last = ctx.name?.lastName || '';
      return `${first} ${last}`.trim() || 'Your Profile';
    },
    evidenceCounts() {
      const ctx = this.profileContext;
      const skills = ctx.skills || {};
      const skillCount = (skills.hard?.length || 0) + (skills.soft?.length || 0) + (skills.technical?.length || 0);
      const workCount = (ctx.workExperience || []).length;
      const achievementCount = (ctx.workExperience || []).reduce((sum, w) => sum + (w.achievements?.length || 0), 0);
      const smallWinCount = (ctx.smallWins || []).length;
      return {
        skills: skillCount,
        workExperience: workCount,
        achievements: achievementCount,
        smallWins: smallWinCount
      };
    },
    completenessPercentage() {
      return this.profileContext.completenessScore || 0;
    },
    completenessColor() {
      const pct = this.completenessPercentage;
      if (pct >= 80) return "success";
      if (pct >= 50) return "primary";
      if (pct >= 25) return "warning";
      return "error";
    },
    missingItems() {
      const missing = [];
      const ctx = this.profileContext;
      if (!ctx.name?.firstName) missing.push('Your name');
      if (!ctx.address?.city) missing.push('Your location');
      if (!(ctx.skills?.hard?.length > 0)) missing.push('Hard skills');
      if (!(ctx.skills?.soft?.length > 0)) missing.push('Soft skills');
      if (!(ctx.workExperience?.length > 0)) missing.push('Work experience');
      if (!(ctx.education?.length > 0)) missing.push('Education');
      if (!ctx.professionalSummary) missing.push('Professional summary');
      if (!(ctx.smallWins?.length > 0)) missing.push('Achievements / wins');
      return missing;
    },
    hasEnoughData() {
      const counts = this.evidenceCounts;
      return counts.workExperience > 0 || counts.achievements > 0 || counts.smallWins > 0;
    },
    profileJobOptions() {
      // Each option is { text: "Title at Company", value: <WorkDict key> } so
      // that when the user picks one of their ACTUAL jobs we can send the job
      // key to the backend and scope Superpowers to exactly that role. Typing a
      // custom/aspirational role yields a plain string (no key) and stays
      // career-wide. value can be "" when a job has no key; we fall back to a
      // title match server-side in that case.
      function isValidTitle(t) {
        var trimmed = (t || "").trim();
        return trimmed && trimmed.toLowerCase() !== "untitled role";
      }
      const jobs = this.profileContext.workExperience || [];
      var results = [];
      var seen = {};
      jobs.forEach(function(j) {
        var company = j.company || "";
        var key = j.key || "";
        var positions = j.positions || [];
        if (positions.length > 0) {
          positions.forEach(function(p) {
            var t = (p.title || p.role || "").trim();
            if (isValidTitle(t)) {
              var label = t;
              if (company) label += " at " + company;
              if (!seen[label]) { seen[label] = true; results.push({ text: label, value: key }); }
            }
          });
        } else {
          var t = (j.title || "").trim();
          if (isValidTitle(t)) {
            var label = t;
            if (company) label += " at " + company;
            if (!seen[label]) { seen[label] = true; results.push({ text: label, value: key }); }
          }
        }
      });
      return results;
    },
    selectedHeadline() {
      if (this.selectedHeadlineIndex >= 0 && this.selectedHeadlineIndex < this.linkedInHeadlines.length) {
        return this.linkedInHeadlines[this.selectedHeadlineIndex];
      }
      return "";
    },
    assembledExperience() {
      var results = [];
      (this.profileContext.workExperience || []).forEach(function(j) {
        var dates = "";
        if (j.startDate) {
          dates = j.startDate;
          if (j.isCurrent) dates += " - Present";
          else if (j.endDate) dates += " - " + j.endDate;
        }
        var company = j.company || "";
        var achBullets = (j.achievements || []).map(function(a) {
          if (typeof a === "string") return a;
          return a.displayCustom || a.displayAuto || a.description || "";
        }).filter(function(t) { return t; });
        var positions = j.positions || [];
        if (positions.length > 0) {
          positions.forEach(function(p) {
            var posTitle = (p.title || p.role || "").trim();
            if (posTitle && posTitle.toLowerCase() !== "untitled role") {
              results.push({
                title: posTitle,
                company: company,
                dates: dates,
                achievements: achBullets
              });
            }
          });
        } else {
          var jobTitle = (j.title || "").trim();
          if (jobTitle && jobTitle.toLowerCase() !== "untitled role") {
            results.push({
              title: jobTitle,
              company: company,
              dates: dates,
              achievements: achBullets
            });
          }
        }
      });
      return results;
    },
    displayExperience() {
      if (this.experienceStyle === "original") return this.assembledExperience;
      var optimized = this.optimizedExperienceData[this.experienceStyle];
      if (!optimized || !optimized.length) return this.assembledExperience;
      var assembled = this.assembledExperience;
      var used = {};
      return assembled.map(function(job) {
        var match = null;
        for (var i = 0; i < optimized.length; i++) {
          if (used[i]) continue;
          if (optimized[i].company === job.company && optimized[i].title === job.title) {
            match = optimized[i];
            used[i] = true;
            break;
          }
        }
        if (!match) {
          for (var k = 0; k < optimized.length; k++) {
            if (used[k]) continue;
            if (optimized[k].company === job.company) {
              match = optimized[k];
              used[k] = true;
              break;
            }
          }
        }
        if (match && match.achievements && match.achievements.length) {
          return {
            title: job.title,
            company: job.company,
            dates: job.dates,
            achievements: match.achievements
          };
        }
        return job;
      });
    },
    assembledSkills() {
      var skills = this.profileContext.skills || {};
      var all = [];
      if (skills.hard) all = all.concat(skills.hard);
      if (skills.soft) all = all.concat(skills.soft);
      if (skills.technical) all = all.concat(skills.technical);
      return all;
    },
    assembledEducation() {
      return (this.profileContext.education || []).map(function(e) {
        return {
          degree: e.degree || e.text || "Degree",
          school: e.school || e.institution || "",
          year: e.year || e.graduationYear || ""
        };
      });
    },
    assembledCertifications() {
      return this.profileContext.certifications || [];
    },
    experienceAsText() {
      return this.displayExperience.map(function(j) {
        var text = j.title + (j.company ? " at " + j.company : "");
        if (j.dates) text += " (" + j.dates + ")";
        if (j.achievements && j.achievements.length) {
          text += "\n" + j.achievements.map(function(a) { return "- " + a; }).join("\n");
        }
        return text;
      }).join("\n\n");
    },
    educationAsText() {
      return this.assembledEducation.map(function(e) {
        var text = e.degree;
        if (e.school) text += " - " + e.school;
        if (e.year) text += " (" + e.year + ")";
        return text;
      }).join("\n");
    },
    projectsAsText() {
      return this.linkedInProjects.map(function(p) {
        return p.projectName + "\n" + p.description;
      }).join("\n\n");
    }
  },
  async mounted() {
    // PR-UPGRADE-MODAL: gating unchanged. A free user should not be on this
    // page, so open the shared upgrade modal and send them back to a surface
    // that works for them (history or CareerProfile fallback).
    if (!this.isPremium && !this.companyViewEnabled) {
      this.$store.dispatch("upgradeModal/open", {
        featureName: "the Career Story Generator",
        message:
          "Career stories and LinkedIn content are part of the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace({ path: "/CareerProfile" });
      }
      return;
    }
    await this.loadProfileContext();
  },
  methods: {
    async loadProfileContext() {
      this.loading = true;
      this.profileError = null;
      try {
        if (!this.resumeId) {
          this.profileError = "No profile selected";
          this.loading = false;
          return;
        }
        const result = await GetProfileContext(this.resumeId);
        if (result.success && result.data) {
          this.profileContext = result.data;
        } else {
          this.profileError = result.msg || "Failed to load profile";
        }
      } catch (err) {
        console.error("Error loading profile context:", err);
        this.profileError = "An error occurred loading your profile";
      } finally {
        this.loading = false;
      }
    },
    goToCapture() {
      this.$router.push("/SmallWinsCapture");
    },
    goToMissing(item) {
      var routes = {
        'Your name': '/Information',
        'Your location': '/Contact',
        'Hard skills': '/HardSkills',
        'Soft skills': '/SoftSkills',
        'Work experience': '/WorkCompanyGeneral',
        'Education': '/EducationEntryGeneral',
        'Professional summary': '/FinalInfo',
        'Achievements / wins': '/Work'
      };
      this.$router.push(routes[item] || '/SmallWinsCapture');
    },
    goToStat(route) {
      this.$router.push(route);
    },
    extractGeneratedText(data, ...fields) {
      if (!data) return "";
      if (typeof data === "string") return data;
      if (data.insufficientData) {
        for (const field of fields) {
          if (data[field]) return data[field];
        }
        return "Your profile doesn't have enough data yet. Add work experience, skills, and achievements to your Career Profile first, then come back to generate your story.";
      }
      for (const field of fields) {
        if (data[field]) return data[field];
      }
      if (data.raw) return data.raw;
      return typeof data === "object" ? JSON.stringify(data) : String(data);
    },
    async generateLinkedInSummary() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateLinkedInSummary({
          targetRole: this.targetRole,
          tone: this.tone
        });
        if (response.success && response.data) {
          this.linkedInSummary = this.extractGeneratedText(response.data, "summary");
          this.showSnackbar("Summary generated!", "success");
        } else {
          this.showSnackbar(response.msg || "Failed to generate summary", "error");
        }
      } catch (error) {
        console.error("Error generating summary:", error);
        this.showSnackbar("Failed to generate summary", "error");
      } finally {
        this.generating = false;
      }
    },
    onOptimizedToggle() {
      if (!this.optimizedExperienceData.optimized) {
        this.optimizeExperience();
      }
    },
    async optimizeExperience() {
      this.generatingExperience = true;
      try {
        const response = await CareerStoryApi.OptimizeExperience({ style: "concise" });
        if (response.success && response.data) {
          if (response.data.insufficientData) {
            this.showSnackbar(response.data.message || "Not enough data to optimize experience", "warning");
            this.experienceStyle = "original";
            return;
          }
          var parsed = response.data;
          if (typeof parsed === "string") {
            try { parsed = JSON.parse(parsed); } catch(e) { /* already object */ }
          }
          var experience = parsed.experience || [];
          this.$set(this.optimizedExperienceData, "optimized", experience);
          this.experienceStyle = "optimized";
          this.showSnackbar("Experience optimized! Click Re-optimize to regenerate after profile changes.", "success");
        } else {
          this.showSnackbar(response.msg || "Failed to optimize experience", "error");
        }
      } catch (error) {
        console.error("Error optimizing experience:", error);
        this.showSnackbar("Failed to optimize experience", "error");
      } finally {
        this.generatingExperience = false;
      }
    },
    async generateElevatorPitch() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateElevatorPitch({
          context: this.pitchContext,
          duration: this.pitchDuration
        });
        if (response.success && response.data) {
          this.elevatorPitch = this.extractGeneratedText(response.data, "pitch");
          this.showSnackbar("Pitch generated!", "success");
        } else {
          this.showSnackbar(response.msg || "Failed to generate pitch", "error");
        }
      } catch (error) {
        console.error("Error generating pitch:", error);
        this.showSnackbar("Failed to generate pitch", "error");
      } finally {
        this.generating = false;
      }
    },
    async generateCareerNarrative() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateCareerNarrative({
          style: this.narrativeStyle,
          includeTransitions: this.includeTransitions
        });
        if (response.success && response.data) {
          this.careerNarrative = this.extractGeneratedText(response.data, "narrative");
          this.showSnackbar("Narrative generated!", "success");
        } else {
          this.showSnackbar(response.msg || "Failed to generate narrative", "error");
        }
      } catch (error) {
        console.error("Error generating narrative:", error);
        this.showSnackbar("Failed to generate narrative", "error");
      } finally {
        this.generating = false;
      }
    },
    async generateLinkedInHeadline() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateLinkedInHeadline({
          targetRole: this.headlineTargetRole || null,
          industry: this.headlineIndustry || null
        });
        if (response.success && response.data) {
          if (response.data.insufficientData) {
            this.showSnackbar(response.data.message || "Not enough profile data to generate headlines", "warning");
            return;
          }
          const data = response.data;
          if (data.headlines && Array.isArray(data.headlines)) {
            this.linkedInHeadlines = data.headlines;
          } else if (typeof data === "object" && data.raw) {
            this.linkedInHeadlines = [data.raw];
          } else {
            this.linkedInHeadlines = [];
          }
          if (this.linkedInHeadlines.length) {
            this.selectedHeadlineIndex = 0;
            this.showSnackbar("Headlines generated!", "success");
          }
        } else {
          this.showSnackbar(response.msg || "Failed to generate headlines", "error");
        }
      } catch (error) {
        console.error("Error generating headlines:", error);
        this.showSnackbar("Failed to generate headlines", "error");
      } finally {
        this.generating = false;
      }
    },
    async generateLinkedInProjects() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateLinkedInProjects();
        if (response.success && response.data) {
          if (response.data.insufficientData) {
            this.showSnackbar(response.data.message || "Not enough data to recommend projects", "warning");
            return;
          }
          const data = response.data;
          if (data.projects && Array.isArray(data.projects)) {
            this.linkedInProjects = data.projects;
          } else {
            this.linkedInProjects = [];
          }
          if (this.linkedInProjects.length) {
            this.showSnackbar(`Found ${this.linkedInProjects.length} project-worthy accomplishments!`, "success");
          } else {
            this.showSnackbar("No standout projects found yet — add more achievements or small wins", "info");
          }
        } else {
          this.showSnackbar(response.msg || "Failed to find projects", "error");
        }
      } catch (error) {
        console.error("Error generating projects:", error);
        this.showSnackbar("Failed to find projects", "error");
      } finally {
        this.generating = false;
      }
    },
    // SUPERPOWERS: fingerprint of the CURRENT profile evidence, computed from the
    // GetProfileContext payload via the SAME shared helper the Career Profile
    // uses, so both surfaces produce byte-identical fingerprints over the same
    // evidence. Used when writing the shared record on career-wide generation.
    buildCareerFingerprint() {
      var ctx = this.profileContext || {};
      var skills = ctx.skills || {};
      var achievements = [];
      (ctx.workExperience || []).forEach(function(w) {
        (w && w.achievements ? w.achievements : []).forEach(function(a) {
          if (a) achievements.push(a);
        });
      });
      return buildSuperpowersFingerprint({
        hardSkills: skills.hard || [],
        softSkills: skills.soft || [],
        achievements: achievements,
        wins: ctx.smallWins || []
      });
    },
    async generateSuperPowers() {
      this.generating = true;
      // CAREER-WIDE vs ROLE: only a career-wide (no targetRole) generation
      // updates the ONE shared superpowers record the Career Profile + export
      // read. A role-specific result must NOT overwrite the career-wide record.
      var isCareerWide = !(
        this.superpowerScope === "role" && this.superpowerTargetRole
      );
      try {
        const options = {};
        if (this.superpowerScope === "role" && this.superpowerTargetRole) {
          // superpowerTargetRole is an object { text, value } when the user picks
          // one of their real jobs (value = WorkDict key), or a plain string when
          // they type a custom/aspirational role. Send the label as targetRole and
          // the key as targetRoleKey so the backend can scope to that exact job.
          var picked = this.superpowerTargetRole;
          if (picked && typeof picked === "object") {
            options.targetRole = picked.text || "";
            if (picked.value) {
              options.targetRoleKey = picked.value;
            }
          } else {
            options.targetRole = picked;
          }
        }
        const response = await CareerStoryApi.GenerateSuperPowers(options);
        if (response.success && response.data) {
          if (response.data.insufficientData) {
            this.superpowers = [];
            this.showSnackbar(response.data.message || "Not enough profile data to identify superpowers", "warning");
            return;
          }
          const data = response.data;
          if (data.superpowers && Array.isArray(data.superpowers)) {
            this.superpowers = data.superpowers;
            // SUPERPOWERS: on a CAREER-WIDE generation, write the ONE shared
            // record (resumation_superpowers_<resumeId>) with the current
            // profile fingerprint so the Career Profile + export reflect it.
            // Role-specific results are intentionally NOT written here.
            if (isCareerWide) {
              writeSuperpowersRecord(
                this.resumeId,
                this.superpowers.slice(0, 5),
                this.buildCareerFingerprint()
              );
            }
            this.showSnackbar(`Identified ${this.superpowers.length} superpower${this.superpowers.length !== 1 ? "s" : ""}!`, "success");
          } else {
            this.superpowers = [];
            this.showSnackbar("Could not identify superpowers - try adding more achievements", "info");
          }
        } else {
          this.showSnackbar(response.msg || "Failed to discover superpowers", "error");
        }
      } catch (error) {
        console.error("Error generating superpowers:", error);
        this.showSnackbar("Failed to discover superpowers", "error");
      } finally {
        this.generating = false;
      }
    },
    copySuperpower(sp) {
      var text = sp.name + "\n" + sp.oneLiner + "\n\nEvidence:\n";
      (sp.evidence || []).forEach(function(ev) { text += "- " + ev + "\n"; });
      if (sp.roles && sp.roles.length > 0) {
        text += "\nWhere it showed up: " + sp.roles.join(", ");
      }
      navigator.clipboard.writeText(text).then(() => {
        this.showSnackbar("Superpower copied to clipboard!", "success");
      });
    },
    copyProjectToClipboard(project) {
      const text = `${project.projectName}\n\n${project.description}`;
      navigator.clipboard.writeText(text).then(() => {
        this.showSnackbar("Project copied to clipboard!", "success");
      });
    },
    async generateBiography() {
      this.generating = true;
      try {
        const response = await CareerStoryApi.GenerateBiography({
          instructions: this.bioInstructions || null,
          length: this.bioLength,
          tone: this.bioTone
        });
        if (response.success && response.data) {
          this.biography = this.extractGeneratedText(response.data, "biography");
          this.showSnackbar("Biography generated!", "success");
        } else {
          this.showSnackbar(response.msg || "Failed to generate biography", "error");
        }
      } catch (error) {
        console.error("Error generating biography:", error);
        this.showSnackbar("Failed to generate biography", "error");
      } finally {
        this.generating = false;
      }
    },
    copyAllLinkedIn() {
      var sections = [];
      if (this.selectedHeadline) {
        sections.push("HEADLINE\n" + this.selectedHeadline);
      }
      if (this.linkedInSummary) {
        sections.push("ABOUT\n" + this.linkedInSummary);
      }
      if (this.assembledExperience.length) {
        sections.push("EXPERIENCE\n" + this.experienceAsText);
      }
      if (this.assembledSkills.length) {
        sections.push("SKILLS\n" + this.assembledSkills.join(" \u00B7 "));
      }
      if (this.assembledEducation.length) {
        sections.push("EDUCATION\n" + this.educationAsText);
      }
      if (this.assembledCertifications.length) {
        sections.push("LICENSES & CERTIFICATIONS\n" + this.assembledCertifications.join("\n"));
      }
      if (this.linkedInProjects.length) {
        sections.push("PROJECTS\n" + this.projectsAsText);
      }
      var fullText = sections.join("\n\n---\n\n");
      navigator.clipboard.writeText(fullText).then(() => {
        this.showSnackbar("All sections copied to clipboard!", "success");
      });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showSnackbar("Copied to clipboard!", "success");
      });
    },
    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}
.theme--dark .bg-grey-lighten-4 {
  background-color: #2a2a3e;
}
.linkedin-guide-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.theme--dark .linkedin-guide-panel {
  border-color: #444;
}
.linkedin-guide-table th {
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.headline-selected {
  border-color: #007AFF !important;
  background-color: #f0f7ff;
}
.theme--dark .headline-selected {
  background-color: rgba(0, 122, 255, 0.15);
}
.info-hint-box {
  background: #f0f7ff;
  border-radius: 4px;
}
.theme--dark .info-hint-box {
  background: rgba(0, 122, 255, 0.08);
}
.stat-chip-clickable {
  cursor: pointer;
}
.stat-chip-clickable:focus-visible {
  outline: 2px solid #007AFF;
  outline-offset: 1px;
}
</style>
